import i18n from '@/utils/i18n';
import {
  OrderGuideCollectionType,
  SalesPortalOrderGuideCollectionsQuery,
} from '@/types';
import { useGeneratedSalesPortalOrderGuideCollectionsQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/sales-portal-order-guide-collections.generated';
import { useMemo } from 'react';

// A collection's origin, as a domain value independent of the generated
// `OrderGuideCollectionType` so consumers never depend on the codegen enum.
export type OrderGuideCollectionKind = 'restaurant' | 'supplier';

const COLLECTION_KIND: Record<
  OrderGuideCollectionType,
  OrderGuideCollectionKind
> = {
  [OrderGuideCollectionType.Restaurant]: 'restaurant',
  [OrderGuideCollectionType.Supplier]: 'supplier',
};

export interface OrderGuideCollectionData {
  displayName: string;
  isDefault: boolean;
  linkedSupplierCollectionUUID: string | null;
  sortOrder: number | null;
  type: OrderGuideCollectionKind;
  uuid: string;
}

type OrderGuideCollection =
  SalesPortalOrderGuideCollectionsQuery['getOrderGuideCollectionsForOrderEntry']['collections'][number];

const transformOrderGuideCollection = (
  collection: OrderGuideCollection,
): OrderGuideCollectionData => ({
  displayName: collection.is_default
    ? i18n.t('sales_portal.guide_selector.default')
    : (collection.name ?? i18n.t('order_guide')),
  isDefault: collection.is_default,
  linkedSupplierCollectionUUID:
    collection.linked_supplier_collection_uuid ?? null,
  sortOrder: collection.sort_order ?? null,
  type: COLLECTION_KIND[collection.type],
  uuid: collection.uuid,
});

/**
 * Loads order guide collections for a restaurant-supplier pair.
 *
 * Fetches available order guides and transforms them into display-ready data,
 * including localized display names and default guide identification.
 *
 * @param restaurantUUID - The UUID of the restaurant
 * @param supplierUUID - The UUID of the supplier
 * @param onCompleted - Optional callback invoked when data loads, useful for
 *   setting initial state without a sync useEffect
 *
 * @returns Object containing:
 *   - `orderGuides` - Array of transformed order guide data
 *   - `defaultOrderGuide` - The default guide, or first guide if none marked default
 *   - `loading` - Whether the query is in flight
 *   - `error` - Any error from the query
 *   - `refetch` - Function to refetch the data
 *
 * @example
 * ```tsx
 * const [selectedGuide, setSelectedGuide] = useState<OrderGuideCollectionData | null>(null);
 *
 * const { orderGuides } = useSalesPortalOrderGuideCollectionsLoader(
 *   restaurantUUID,
 *   supplierUUID,
 *   (guides) => {
 *     const defaultGuide = guides.find((g) => g.isDefault) ?? guides[0];
 *     setSelectedGuide(defaultGuide);
 *   },
 * );
 * ```
 */
export const useSalesPortalOrderGuideCollectionsLoader = (
  restaurantUUID: string,
  supplierUUID: string,
  onCompleted?: (orderGuides: OrderGuideCollectionData[]) => void,
) => {
  const { data, loading, error, refetch } =
    useGeneratedSalesPortalOrderGuideCollectionsQuery({
      onCompleted: onCompleted
        ? (data) => {
            const guides =
              data?.getOrderGuideCollectionsForOrderEntry?.collections?.map(
                transformOrderGuideCollection,
              ) ?? [];
            onCompleted(guides);
          }
        : undefined,
      variables: {
        restaurantUUID,
        supplierUUID,
      },
    });

  const orderGuides = useMemo((): OrderGuideCollectionData[] => {
    if (!data?.getOrderGuideCollectionsForOrderEntry?.collections) {
      return [];
    }

    return data.getOrderGuideCollectionsForOrderEntry.collections.map(
      transformOrderGuideCollection,
    );
  }, [data]);

  const defaultOrderGuide = useMemo((): OrderGuideCollectionData | null => {
    if (orderGuides.length === 0) {
      return null;
    }
    return orderGuides.find((c) => c.isDefault) ?? orderGuides[0];
  }, [orderGuides]);

  return {
    defaultOrderGuide,
    error,
    loading,
    orderGuides,
    refetch,
  };
};
